import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../utils/supabase';
import { getCache, setCache } from '../../utils/cache';
import ReflectionTextContent from '../ReflectionTextContent';

function parseIntervalToMs(interval) {
  const dayMatch = String(interval || '').match(/(\d+)\s+days?/);
  const timeMatch = String(interval || '').match(/(\d{1,2}):(\d{2}):(\d{2})/);
  let ms = 0;
  if (dayMatch) ms += parseInt(dayMatch[1], 10) * 24 * 60 * 60 * 1000;
  if (timeMatch) {
    ms += parseInt(timeMatch[1], 10) * 60 * 60 * 1000;
    ms += parseInt(timeMatch[2], 10) * 60 * 1000;
    ms += parseInt(timeMatch[3], 10) * 1000;
  }
  return ms || (7 * 24 * 60 * 60 * 1000);
}

function formatReflectionDate(date) {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}

function groupRowsByQuestionAndCategory(rows) {
  const questions = new Map();
  (rows || []).forEach((r) => {
    const questionId = String(r.reflection_question_id || r?.reflection_questions?.question_id || r?.id || '');
    const prompt = r?.reflection_questions?.prompt_text || 'Untitled question';
    const category = r?.category || 'Uncategorized';
    if (!questions.has(questionId)) {
      questions.set(questionId, { prompt, categories: new Map() });
    }
    const entry = questions.get(questionId);
    if (!entry.categories.has(category)) entry.categories.set(category, []);
    entry.categories.get(category).push(r);
  });
  return { questions };
}

function countResponses(optMap) {
  return Array.from(optMap.values()).reduce((sum, rows) => sum + rows.length, 0);
}

function ReflectionResponses({ responses, usersById, onUserClick }) {
  return responses.map((ref) => {
    const user = usersById[ref.user_id];
    const userName = user?.full_name || user?.email || 'Unknown user';
    return (
      <div key={ref.id} className="bootcamp-reflection-entry">
        <button
          type="button"
          className="bootcamp-reflection-user-link"
          onClick={() => onUserClick?.(ref.user_id)}
        >
          {userName}
        </button>
        <div className="bootcamp-reflection-meta">{formatReflectionDate(ref.submitted_at)}</div>
        <ReflectionTextContent reflection={ref} primaryClassName="bootcamp-reflection-text" />
      </div>
    );
  });
}

function QuestionGroup({ prompt, optMap, usersById, isOpen, onToggle, onUserClick }) {
  const entries = Array.from(optMap.entries());
  const hasOnlyUncategorized = entries.every(([category]) => category === 'Uncategorized');
  const responseCount = countResponses(optMap);

  return (
    <div className="bootcamp-reflection-question">
      <div className="bootcamp-reflection-question-header">
        <h4 className="bootcamp-reflection-question-title">{prompt}</h4>
        <button type="button" className="analytics-expand-link" onClick={onToggle}>
          {isOpen
            ? 'Hide responses'
            : `Show ${responseCount} response${responseCount !== 1 ? 's' : ''}`}
        </button>
      </div>
      {isOpen && (
        hasOnlyUncategorized ? (
          <div className="modern-list-panel bootcamp-reflection-panel">
            <ReflectionResponses responses={entries.flatMap(([, res]) => res)} usersById={usersById} onUserClick={onUserClick} />
          </div>
        ) : (
          entries.map(([category, responses]) => (
            <div key={category} className="bootcamp-reflection-category">
              {category !== 'Uncategorized' && (
                <div className="bootcamp-reflection-category-label">{category}</div>
              )}
              <div className="modern-list-panel bootcamp-reflection-panel">
                <ReflectionResponses responses={responses} usersById={usersById} onUserClick={onUserClick} />
              </div>
            </div>
          ))
        )
      )}
    </div>
  );
}

function BootcampReflectionsSection({ bootcampId, bootcamp, lessons = [], initialUsers = [], onUserClick }) {
  const [openLessons, setOpenLessons] = useState({});
  const [openQuestions, setOpenQuestions] = useState({});
  const [lessonData, setLessonData] = useState({});
  const [users, setUsers] = useState(initialUsers);

  useEffect(() => {
    setUsers(initialUsers);
  }, [initialUsers]);

  const reflectionLessons = useMemo(() => {
    if (!bootcamp) return [];
    const now = Date.now();
    const stutterMs = parseIntervalToMs(bootcamp.stutter_time);
    const bootcampStart = bootcamp.start_date ? new Date(bootcamp.start_date).getTime() : 0;
    return (lessons || [])
      .filter((l) => l.lesson_type !== 'onboarding')
      .slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .filter((l) => {
        const pos = Number(l.position || 1);
        const start = bootcampStart + ((pos - 1) * stutterMs);
        return now >= start;
      });
  }, [bootcamp, lessons]);

  const usersById = useMemo(() => {
    const map = {};
    (users || []).forEach((u) => {
      if (u?.id) map[u.id] = u;
    });
    return map;
  }, [users]);

  const loadLesson = async (lessonId) => {
    const CACHE_KEY = `bootcamp.${bootcampId}.reflections.lesson.${lessonId}.v2`;
    const ttlMs = 2 * 60 * 1000;
    const cached = getCache(CACHE_KEY, ttlMs);
    if (cached && Array.isArray(cached.rows)) {
      const grouped = groupRowsByQuestionAndCategory(cached.rows);
      setLessonData((prev) => ({ ...prev, [lessonId]: { rows: cached.rows, grouped, loading: false, error: '' } }));
      return;
    }

    const baseSelect = `id, user_id, bootcamp_id, reflection_question_id, reflection_text, follow_up_question, follow_up_answer, category, submitted_at,
      reflection_questions:reflection_questions!inner(
        question_id, prompt_text,
        questions:questions!inner(lesson_id)
      )`;
    const selectWithUsers = `${baseSelect}, users:users!inner(id,full_name,email)`;

    try {
      setLessonData((prev) => ({ ...prev, [lessonId]: { ...(prev[lessonId] || {}), loading: true, error: '' } }));
      const { data: rows, error: err } = await supabase
        .from('user_reflections')
        .select(users.length > 0 ? baseSelect : selectWithUsers)
        .eq('bootcamp_id', bootcampId)
        .eq('reflection_questions.questions.lesson_id', lessonId);
      if (err) {
        setLessonData((prev) => ({
          ...prev,
          [lessonId]: { ...(prev[lessonId] || {}), loading: false, error: 'Failed to load reflections' },
        }));
        return;
      }
      if (!users.length && Array.isArray(rows)) {
        const nextUsers = {};
        rows.forEach((r) => {
          if (r.users?.id) {
            nextUsers[r.users.id] = {
              id: r.users.id,
              full_name: r.users.full_name,
              email: r.users.email,
            };
          }
        });
        setUsers(Object.values(nextUsers));
      }
      const grouped = groupRowsByQuestionAndCategory(rows || []);
      setLessonData((prev) => ({ ...prev, [lessonId]: { rows: rows || [], grouped, loading: false, error: '' } }));
      setCache(CACHE_KEY, { rows: rows || [] });
    } catch {
      setLessonData((prev) => ({
        ...prev,
        [lessonId]: { ...(prev[lessonId] || {}), loading: false, error: 'Failed to load reflections' },
      }));
    }
  };

  const toggleLesson = async (lesson) => {
    const lessonId = lesson.lesson_id;
    const isOpen = !!openLessons[lessonId];
    setOpenLessons((prev) => ({ ...prev, [lessonId]: !isOpen }));
    if (!isOpen && !lessonData[lessonId]) {
      await loadLesson(lessonId);
    }
  };

  const questionKey = (lessonId, qId) => `${lessonId}::${qId}`;

  const toggleQuestion = (lessonId, qId) => {
    const key = questionKey(lessonId, qId);
    setOpenQuestions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (reflectionLessons.length === 0) {
    return (
      <div className="modern-list-panel">
        <div className="modern-list-empty">No unlocked lessons with reflections yet.</div>
      </div>
    );
  }

  return (
    <div className="bootcamp-reflections-section">
      {reflectionLessons.map((lesson) => {
        const isOpen = !!openLessons[lesson.lesson_id];
        const data = lessonData[lesson.lesson_id];
        const lessonTitle = lesson.title
          ? `Checkpoint ${lesson.position} · ${lesson.title}`
          : `Checkpoint ${lesson.position}`;

        return (
          <div key={lesson.lesson_id} className="analytics-ranked-group">
            <div className="modern-list-panel bootcamp-reflection-lesson-panel">
              <div className="bootcamp-reflection-lesson-header">
                <h3 className="analytics-ranked-group-title">{lessonTitle}</h3>
                <button
                  type="button"
                  className="analytics-expand-link"
                  onClick={() => toggleLesson(lesson)}
                >
                  {isOpen ? 'Hide questions' : 'Show questions'}
                </button>
              </div>
              {isOpen && (
                <div className="bootcamp-reflection-lesson-body">
                  {!data || data.loading ? (
                    <div className="modern-list-empty">Loading reflections…</div>
                  ) : data.error ? (
                    <div className="modern-error bootcamp-reflection-lesson-error">{data.error}</div>
                  ) : !data.grouped || (data.grouped.questions?.size || 0) === 0 ? (
                    <div className="modern-list-empty">No reflections for this lesson.</div>
                  ) : (
                    <div className="bootcamp-reflection-questions-panel">
                      {Array.from(data.grouped.questions.entries()).map(([qId, { prompt, categories }]) => (
                        <QuestionGroup
                          key={qId}
                          prompt={prompt}
                          optMap={categories}
                          usersById={usersById}
                          isOpen={!!openQuestions[questionKey(lesson.lesson_id, qId)]}
                          onToggle={() => toggleQuestion(lesson.lesson_id, qId)}
                          onUserClick={onUserClick}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default BootcampReflectionsSection;
